import { setupLayouts } from 'virtual:generated-layouts'
import { createRouter, createWebHistory } from 'vue-router'
import { isUserLoggedIn } from './utils'
import routes from '~pages'
import { useRootStore } from '@/stores/root'
import { useUserStore } from '@/stores/user'
import { isBionicSite } from '@/utils/whiteLabelHelpers'
import { signInWithCustomToken } from 'firebase/auth'
import { auth } from '@/firebase'
import { User } from '@/models/user'
import { getCurrentIp, getUser } from '@/services/userService'
import { transformToUserProfileModel } from '@/utils/objectTransformers/user'

/**
 * Checks if 2FA is required for the current user and route
 * Sets ipVerified flag when IP is verified to avoid future checks
 * @param userStore - The user store instance
 * @param to - The target route
 * @param from - The source route
 * @returns Redirect object if 2FA is required, null otherwise
 */
async function check2FARequirement(userStore: any, to: any, from: any) {
  const user = userStore.userProfile

  // Early return if 2FA is not enabled for this white-label platform
  const isWL2FAEnabled = user?.wlPlatformOfCustomer?.enable2FA === true
  if (!isWL2FAEnabled) {
    // 2FA not required, mark IP as verified
    userStore.setIpVerified(true)
    return null
  }

  // Skip 2FA for admin accounts
  const isAdminAccount =
    userStore.adminProfile?.accountType === 'admin' ||
    user?.accountType === 'admin'
  if (isAdminAccount) {
    // Admin accounts don't need 2FA, mark IP as verified
    userStore.setIpVerified(true)
    return null
  }

  // Skip 2FA for specific routes
  const skip2FARoutes = ['auth-2fa']
  const skip2FASubjects = ['Verification', 'VerifyCheckout']

  if (
    skip2FARoutes.includes(to.name) ||
    skip2FASubjects.includes(to.meta.subject)
  ) {
    // On 2FA route or verification routes, don't set ipVerified yet
    // Let the 2FA success handler set it when verification is complete
    return null
  }

  // Check if user is already verified with current IP
  const domain = window.location.origin
  const currentIP = await getCurrentIp()
  const expectedIP = domain?.includes('localhost') ? '::1' : currentIP

  const isUserVerified =
    user?.is2FAVerified && user?.lastVerifiedIP === expectedIP

  if (isUserVerified) {
    // User is already verified, mark IP as verified
    userStore.setIpVerified(true)
    return null
  }

  // Only redirect if navigating to a different route
  if (to.fullPath !== from.fullPath) {
    console.log('[2FA] Redirecting to auth-2fa...', from)
    return {
      name: 'auth-2fa',
      query: { to: to.fullPath || '/' },
    }
  }

  return null
}

/**
 * Vue Router instance with layout support and scroll behavior
 * - Uses virtual layouts for dynamic route generation
 * - Scrolls to top on route changes
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...setupLayouts(routes)],
  scrollBehavior() {
    return { top: 0 }
  },
})

/**
 * Global navigation guard that handles authentication, 2FA, and route protection
 * Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
 */
router.beforeEach(async (to, from) => {
  // Handle route redirects defined in meta
  if (to.meta.redirectTo) {
    return to.meta.redirectTo
  }

  // Initialize stores
  const rootStore = useRootStore()
  const userStore = useUserStore()

  // Handle white-label customer visiting functionality
  if (to.query.visiting) {
    localStorage.setItem('customer-uuid', <string>to.query.visiting)
    userStore.visitedUID = String(to.query.visiting)
  } else if (localStorage.getItem('customer-uuid')) {
    userStore.visitedUID = localStorage.getItem('customer-uuid')
  }

  // Check user authentication status
  const isLoggedIn: any = await isUserLoggedIn()

  // 2FA Implementation - Check if user needs 2FA verification
  if (isLoggedIn && !userStore.adminProfile) {
    // Only check 2FA if IP hasn't been verified yet
    if (!userStore.ipVerified) {
      const shouldRedirectTo2FA = await check2FARequirement(userStore, to, from)
      if (shouldRedirectTo2FA) {
        return shouldRedirectTo2FA
      }
    }
  }

  // Handle Firebase custom token authentication
  if (to.query.loginToken) {
    try {
      await signInWithCustomToken(auth, to.query?.loginToken as string)
      await getUser()
        .then(async (userProfile: User) => {
          userStore.setUserProfile(transformToUserProfileModel(userProfile))
          // Reset IP verification for new login session
          userStore.setIpVerified(false)
        })
        .catch(() => {
          userStore.logout()
        })
      return '/sites'
    } catch (e) {
      // Authentication failed, continue with normal flow
    }
  }

  // Route protection based on meta subjects
  if (to.meta.subject === 'Auth') {
    // Redirect authenticated users away from auth pages
    if (isLoggedIn) {
      return '/'
    }
  } else if (to.meta.subject === 'Guest') {
    // Allow guest access
    return true
  } else if (to.meta.subject === 'VerifyCheckout') {
    // Allow checkout verification
    return true
  } else {
    // Prevent site creation if user has unpaid invoices
    if (to.path === '/sites/add' && userStore.userHasUnpaidInvoice) {
      rootStore.showSnackbar(
        'You have an unpaid invoice. Please pay it first to create a new site.',
        'error',
      )
      return '/'
    }

    // Restrict access to certain routes for white-label customers
    if (
      (to.path === '/affiliate' ||
        to.path === '/white-label' ||
        (userStore.wlPlatformOfCustomer?.hide_support_form &&
          to.path === '/support')) &&
      userStore.isWLCustomer &&
      !isBionicSite()
    ) {
      return '/'
    }

    // Restrict billing access for white-label customers with offline billing or WL owners
    if (
      to.path === '/billing' &&
      ((userStore.isWLCustomer &&
        userStore.wlPlatformOfCustomer?.is_offline_billing_enabled === true) ||
        userStore.isWLOwnerAndLoggedInAsCustomer)
    ) {
      return '/profile'
    }

    // Handle unauthenticated users
    if (!isLoggedIn) {
      return {
        name: 'login',
        query: {
          to:
            to.name !== 'index'
              ? to.fullPath.replace('visiting', 'user')
              : undefined,
        },
      }
    }
    // Handle white-label owner logged in as customer
    else if (
      userStore.isWLOwnerAndLoggedInAsCustomer &&
      to.name !== 'sites-site_id'
    ) {
      // Clear customer visiting state and switch back to admin context
      localStorage.removeItem('customer-uuid')
      localStorage.removeItem('wl-owner-visiting-customer')
      userStore.visitedUID = null
      userStore.wlVisitingCustomer = false
      try {
        const user = await userStore.updateUser()
        if (user) {
          userStore.replaceAdminWithUser(user)
          userStore.wlPlatformOfCustomer = user.wlPlatformOfCustomer
          userStore.wlDetails = user.wlDetails
          // Reset IP verification when switching user context
          userStore.setIpVerified(false)
        }
      } catch (e) {
        // Handle user update error silently
      }
      rootStore.showSnackbar(
        'This path is not allowed for the current user. Redirecting to the white-label dashboard.',
      )
      return '/white-label'
    }
    // Redirect admin users to admin dashboard if not already there
    else if (
      to.meta.subject !== 'Admin' &&
      !to.fullPath.includes('admin') &&
      isLoggedIn?.accountType === 'admin'
    ) {
      return {
        name: 'admin',
      }
    }
    // Prevent regular users from accessing admin routes
    else if (
      isLoggedIn?.accountType === 'user' &&
      to.meta.subject === 'Admin'
    ) {
      return {
        name: 'index',
      }
    }
    // Check API access permission for API tokens page
    else if (to.name === 'api-tokens' || to.path === '/api-tokens') {
      const userProfile = userStore.userProfile
      // Check if user has API access permission
      if (!userProfile || !userProfile.has_api_access) {
        rootStore.showSnackbar(
          'You do not have permission to access API tokens',
          'error',
        )
        return '/'
      }
    }
  }

  // Allow navigation to proceed
  return true
})

/**
 * Global after navigation guard - cleanup after route changes
 */
router.afterEach(() => {
  const rootStore = useRootStore()
  rootStore.showFullscreenLoader = false
})

export default router
